import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, X } from 'lucide-react';
import { useWatchlistDeliveryStore } from '../hooks/useWatchlistStream.js';
import { useTimezone } from '../hooks/useTimezone.js';
import { usePlayerStore } from '../lib/stores.js';
import { watchlistApi } from '../lib/api.js';
import { formatTime } from '../lib/time.js';
import styles from './PremiereBanner.module.css';

const AUTO_CONFIRM_SECONDS = 10;

export default function WatchlistBanner() {
  const primary = useWatchlistDeliveryStore((s) => s.primary);
  const alerts = useWatchlistDeliveryStore((s) => s.alerts);
  const dismiss = useWatchlistDeliveryStore((s) => s.dismiss);
  const selectChannel = usePlayerStore((s) => s.selectChannel);
  const tz = useTimezone();
  const navigate = useNavigate();

  const [seconds, setSeconds] = useState(AUTO_CONFIRM_SECONDS);
  const lastIdRef = useRef(null);

  useEffect(() => {
    if (!primary) return;
    if (primary.id !== lastIdRef.current) {
      lastIdRef.current = primary.id;
      // primary only ever comes through with auto_switch=true (the backend
      // filters), but guard anyway in case the contract changes.
      setSeconds(primary.auto_switch ? AUTO_CONFIRM_SECONDS : 0);
    }
  }, [primary]);

  useEffect(() => {
    if (!primary || !primary.auto_switch || seconds <= 0) return undefined;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [primary, seconds]);

  useEffect(() => {
    if (primary?.auto_switch && seconds === 0) {
      performSwitch(primary);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary, seconds]);

  function ackQuiet(itemId) {
    // Fire-and-forget — best effort. If the ack fails the worst that
    // happens is the banner reappears on next SSE connect, which is
    // also the recovery path for a closed tab, so this is intentional.
    watchlistApi.ack(itemId).catch(() => {});
  }

  function performSwitch(item) {
    const ch = item.channel;
    if (ch?.id) {
      selectChannel({
        id: ch.id,
        name: ch.name,
        slug: ch.slug,
        logo_url: ch.logo_url,
      });
      navigate('/');
    }
    ackQuiet(item.id);
    dismiss();
  }

  function dismissPrimary() {
    if (primary?.id) ackQuiet(primary.id);
    dismiss();
  }

  if (!primary && (!alerts || alerts.length === 0)) return null;

  return (
    <div className={styles.stack} style={{ bottom: 'auto', top: 80 }}>
      {primary && (
        <div className={styles.banner}>
          <button className={styles.close} onClick={dismissPrimary} aria-label="Dismiss">
            <X size={14} />
          </button>
          <div className={styles.head}>
            <Bookmark size={14} /> From your schedule
          </div>
          <div className={styles.title}>{primary.program.title}</div>
          <div className={styles.meta}>
            {primary.channel?.name} · starts {formatTime(primary.program.start_at, tz)}
          </div>
          {primary.auto_switch && seconds > 0 && (
            <div className={styles.countdown}>
              Switching automatically in <strong>{seconds}</strong>s
            </div>
          )}
          <div className={styles.actions}>
            <button className="btn primary" onClick={() => performSwitch(primary)}>
              Switch now
            </button>
            <button className="btn ghost" onClick={dismissPrimary}>Not now</button>
          </div>
        </div>
      )}
      {alerts && alerts.length > 0 && alerts.map((item) => (
        <div key={item.id} className={`${styles.banner} ${styles.alert}`}>
          <div className={styles.head}><Bookmark size={14} /> Starting soon</div>
          <div className={styles.title}>{item.program.title}</div>
          <div className={styles.meta}>
            {item.channel?.name} · {formatTime(item.program.start_at, tz)}
          </div>
          <div className={styles.actions}>
            <button className="btn" onClick={() => performSwitch(item)}>Switch now</button>
          </div>
        </div>
      ))}
    </div>
  );
}
